import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Search, Gavel, Info, HandHeart } from "lucide-react";
import { ShareButton, guideSharePayload } from "@/components/share/ShareButton";

/**
 * Static reference page — "Tabella delle Maggioranze Assembleari".
 * Content lives in this component only (no DB / demo data written).
 * Reading UX:
 *   - single wide table with 5 columns
 *   - horizontal scroll on smartphone (as requested)
 *   - sticky column headers, sticky "Argomento" column on horizontal scroll
 */

// All Costitutivo values are identical for every entry: 667 (334) / 667 (334).
// Only Deliberativo varies per row.
const COST_COND_1 = "667";
const COST_COND_2 = "334";
const COST_MILL_1 = "667";
const COST_MILL_2 = "334";

// Deliberativo — condòmini text stays constant across convocations ("(idem)")
// so we store one string. Millesimi split into m1 (1ª) + m2 (2ª).
const ROWS = [
    { topic: "Amministratore — compenso", ref: "art. 1135", dc: "maggioranza intervenuti", m1: "500", m2: "334" },
    { topic: "Amministratore — nomina, conferma e revoca", ref: "art. 1136", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
    { topic: "Amministratore — diniego di rinnovazione tacita", ref: "art. 1129", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
    { topic: "Antenne satellitari", ref: "D.L. n. 5/01", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
    { topic: "Ascensore — superamento barriere architettoniche", ref: "L. n. 13/89", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
    { topic: "Costituzione o trasferimento dei diritti reali", ref: "art. 1108", dc: "consenso totalità condòmini", m1: "1000", m2: "idem" },
    { topic: "Deliberazioni senza maggioranze espressamente previste", dc: "maggioranza intervenuti", m1: "500", m2: "334" },
    { topic: "Deliberazioni che modificano destinazioni d'uso parti comuni", ref: "art. 1117-ter", dc: "4/5 condòmini", m1: "800", m2: "idem" },
    { topic: "Deliberazioni tutela destinazioni d'uso", ref: "art. 1117-quater", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
    { topic: "Deliberazioni per collaborazione a progetti territoriali", ref: "art. 1135", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
    { topic: "Impianti non centralizzati — modalità alternative di esecuzione", ref: "art. 1122-bis", dc: "maggioranza intervenuti", m1: "667", m2: "idem" },
    { topic: "Impianti non centralizzati — adeguamento esistenti", ref: "art. 155-bis disp. att. c.c.", dc: "maggioranza intervenuti", m1: "500", m2: "334" },
    { topic: "Impianto di riscaldamento — distacco con squilibri", ref: "art. 1118", dc: "consenso totalità condòmini", m1: "1000", m2: "idem" },
    { topic: "Impianto di riscaldamento — trasformazione per contenimento energetico", ref: "L. n. 10/91", dc: "maggioranza intervenuti", m1: "334", m2: "idem" },
    { topic: "Impianto di riscaldamento — termoregolazione e contabilizzazione", ref: "L. n. 10/91", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
    { topic: "Infrastrutture ricarica elettrica veicoli", ref: "D.L. n. 83/12", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
    { topic: "Innovazioni gravose o voluttuarie", ref: "art. 1121", dc: "maggioranza intervenuti", m1: "667", m2: "idem" },
    { topic: "Innovazioni per passaggio cavi fibra ottica", ref: "L. n. 69/09", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
    { topic: "Innovazioni che rendono parti comuni inservibili", ref: "art. 1120", dc: "consenso totalità condòmini", m1: "1000", m2: "idem" },
    { topic: "Innovazioni — uso più comodo o miglioramento cose comuni", ref: "art. 1120", dc: "maggioranza intervenuti", m1: "667", m2: "idem" },
    { topic: "Innovazioni — sicurezza, barriere architettoniche, energia, parcheggi, fonti rinnovabili, impianti centralizzati", ref: "art. 1120", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
    { topic: "Internet — attivazione sito per consultazione documenti", ref: "art. 71-ter disp. att. c.c.", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
    { topic: "Interventi di recupero su immobile composto da più unità", ref: "L. n. 457/78", dc: "maggioranza condòmini", m1: "500", m2: "idem" },
    { topic: "Interventi contenimento consumo energetico con attestato", ref: "L. n. 10/91", dc: "maggioranza intervenuti", m1: "334", m2: "idem" },
    { topic: "Ipoteca per ricostruzione o miglioramento cosa comune", ref: "art. 1108", dc: "maggioranza condòmini", m1: "667", m2: "idem" },
    { topic: "Liti attive e passive su materie esorbitanti", ref: "art. 1136", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
    { topic: "Locazioni ultranovennali", ref: "art. 1108", dc: "consenso totalità condòmini", m1: "1000", m2: "idem" },
    { topic: "Manutenzione ordinaria", dc: "maggioranza intervenuti", m1: "500", m2: "334" },
    { topic: "Manutenzione straordinaria — riparazioni di notevole entità", ref: "art. 1136", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
    { topic: "Mediazione — legittimazione amministratore", ref: "art. 71-quater", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
    { topic: "Mediazione — approvazione proposta conciliativa", ref: "art. 71-quater", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
    { topic: "Parcheggi sotterranei o al piano terreno", ref: "L. n. 122/89", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
    { topic: "Portierato — soppressione, istituzione o modifica", dc: "maggioranza intervenuti", m1: "667", m2: "idem" },
    { topic: "Regolamento — sanzioni per infrazioni", ref: "art. 70 disp. att. c.c.", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
    { topic: "Regolamento — approvazione e modifiche", ref: "art. 1138", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
    { topic: "Regolamento contrattuale — modifiche diritti soggettivi", dc: "consenso totalità condòmini", m1: "1000", m2: "idem" },
    { topic: "Rendiconto annuale — approvazione", ref: "art. 1135", dc: "maggioranza intervenuti", m1: "500", m2: "334" },
    { topic: "Residui attivi — impiego", ref: "art. 1135", dc: "maggioranza intervenuti", m1: "500", m2: "334" },
    { topic: "Revisore condominiale — nomina", ref: "art. 1130-bis", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
    { topic: "Ricostruzione edificio — perimento inferiore a 3/4", ref: "art. 1128", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
    { topic: "Ricostruzione edificio — perimento totale o superiore a 3/4", ref: "art. 1128", dc: "consenso totalità condòmini", m1: "1000", m2: "idem" },
    { topic: "Scioglimento — divisione in parti autonome", ref: "art. 61 disp. att. c.c.", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
    { topic: "Scioglimento — modifiche per rendere possibile la divisione", ref: "art. 62 disp. att. c.c.", dc: "maggioranza intervenuti", m1: "667", m2: "idem" },
    { topic: "Sopraelevazione — autorizzazione in caso di divieto", ref: "art. 1127", dc: "consenso totalità condòmini", m1: "1000", m2: "idem" },
    { topic: "Spese — ripartizione", ref: "art. 1135", dc: "maggioranza intervenuti", m1: "500", m2: "334" },
    { topic: "Supercondominio — designazione del rappresentante", ref: "art. 67 disp. att. c.c.", dc: "maggioranza intervenuti", m1: "667", m2: "idem" },
    { topic: "Tabella valori millesimali — formazione, rettifica o modifica", dc: "consenso totalità condòmini", m1: "1000", m2: "idem" },
    { topic: "Tabelle millesimali spese — formazione", ref: "art. 69 disp. att. c.c.", dc: "consenso totalità condòmini", m1: "1000", m2: "idem" },
    { topic: "Tabelle millesimali spese — rettifica senza errori o mutamenti rilevanti", ref: "art. 69 disp. att. c.c.", dc: "consenso totalità condòmini", m1: "1000", m2: "idem" },
    { topic: "Tabelle millesimali spese — rettifica per errori o mutamenti rilevanti", ref: "art. 69 disp. att. c.c.", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
    { topic: "Vendita o cessione di beni", ref: "art. 1108", dc: "consenso totalità condòmini", m1: "1000", m2: "idem" },
    { topic: "Videosorveglianza — installazione su parti condominiali", ref: "art. 1122-ter", dc: "maggioranza intervenuti", m1: "500", m2: "idem" },
];

function toneOfMillesimi(v) {
    if (v === "1000") return "text-rose-700";
    if (v === "800") return "text-fuchsia-700";
    if (v === "667") return "text-primary";
    if (v === "500") return "text-amber-800";
    if (v === "334") return "text-emerald-700";
    return "text-slate-700";
}

export default function MaggioranzeAssembleari() {
    const [query, setQuery] = useState("");

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return ROWS;
        return ROWS.filter(r =>
            r.topic.toLowerCase().includes(q) ||
            (r.ref || "").toLowerCase().includes(q) ||
            r.dc.toLowerCase().includes(q)
        );
    }, [query]);

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            <Button asChild variant="ghost" size="sm" className="-ml-2 mb-3 text-muted-foreground" data-testid="magg-back">
                <Link to="/"><ArrowLeft className="size-4 mr-1" /> Torna alla Home</Link>
            </Button>

            <div className="text-xs uppercase tracking-widest text-primary font-semibold inline-flex items-center gap-1.5">
                <Gavel className="size-3.5" /> Guida rapida
            </div>
            <h1
                className="mt-1 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight"
                data-testid="maggioranze-title"
            >
                Tabella delle Maggioranze<br className="sm:hidden" /> Assembleari
            </h1>

            {/* Discreet peer-help share strip — under the title */}
            <div
                data-testid="guide-share-block"
                className="mt-5 rounded-xl border border-primary/15 bg-primary/[0.04] px-4 sm:px-5 py-3 sm:py-3.5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
            >
                <div className="flex items-start sm:items-center gap-2.5 flex-1 min-w-0">
                    <HandHeart className="size-4 text-accent shrink-0 mt-0.5 sm:mt-0" strokeWidth={2.4} />
                    <p className="text-[13.5px] sm:text-sm text-slate-700 leading-snug">
                        <span className="font-semibold text-slate-900">Aiuta chi ne ha bisogno.</span>{" "}
                        Conosci qualcuno con lo stesso dubbio? Condividi questa guida.
                    </p>
                </div>
                <ShareButton
                    payload={guideSharePayload({
                        path: "/faq/maggioranze-assembleari",
                        title: "Tabella delle Maggioranze Assembleari — CondominioNow",
                    })}
                    variant="compact"
                    label="Condividi"
                    data-testid="guide-share-maggioranze"
                    className="shrink-0"
                />
            </div>

            {/* Requested top note */}
            <div
                data-testid="maggioranze-note"
                className="mt-6 rounded-2xl border-l-4 border-primary bg-primary/5 px-4 py-3 sm:px-5 sm:py-4"
            >
                <div className="flex items-start gap-3">
                    <Info className="size-4 sm:size-5 text-primary shrink-0 mt-0.5" />
                    <p className="text-sm sm:text-base text-slate-800 leading-relaxed">
                        Il termine <strong>condòmini</strong> si riferisce ai partecipanti al condominio; il termine <strong>intervenuti</strong> si riferisce ai partecipanti all'assemblea.
                    </p>
                </div>
            </div>

            {/* Full legend requested by user */}
            <div
                data-testid="maggioranze-legend"
                className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-4 sm:px-5 sm:py-5"
            >
                <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-3">Legenda</div>
                <ul className="space-y-2 text-sm text-slate-700 leading-relaxed">
                    <li>
                        <strong className="text-slate-900">Quorum costitutivo:</strong> numero minimo di partecipanti e millesimi necessari affinché l'assemblea sia validamente costituita.
                    </li>
                    <li>
                        <strong className="text-slate-900">Quorum deliberativo:</strong> numero minimo di voti e millesimi necessari affinché una delibera sia approvata.
                    </li>
                    <li><strong className="text-slate-900">N.B.</strong> I valori millesimali riportati nella tabella al di fuori delle parentesi si riferiscono ai quorum costitutivi e deliberativi previsti per la <strong>prima convocazione</strong> dell'assemblea; quelli indicati <strong>tra parentesi</strong> si riferiscono ai quorum costitutivi e deliberativi previsti per la <strong>seconda convocazione</strong>.</li>
                    <li>
                        <em>"Condòmini"</em> = partecipanti al condominio. <em>"Intervenuti"</em> = partecipanti all'assemblea.
                    </li>
                    <li className="flex flex-wrap gap-x-4 gap-y-1 pt-1 border-t border-slate-100 mt-2">
                        <span><strong className="text-rose-700">1000</strong> millesimi = unanimità</span>
                        <span><strong className="text-primary">667</strong> millesimi = 2/3</span>
                        <span><strong className="text-amber-800">500</strong> millesimi = maggioranza semplice</span>
                        <span><strong className="text-emerald-700">334</strong> millesimi = 1/3</span>
                    </li>
                </ul>
            </div>

            {/* Filter */}
            <div className="mt-5 relative">
                <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <Input
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder="Filtra per argomento o articolo (es. amministratore, 1120, sopraelevazione)…"
                    className="pl-9 h-11"
                    data-testid="maggioranze-search"
                />
            </div>

            {/* Empty state */}
            {filtered.length === 0 && (
                <div className="mt-8 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 py-14 text-center text-muted-foreground">
                    Nessuna voce trovata per "{query}".
                </div>
            )}

            {/* Scroll hint on mobile */}
            {filtered.length > 0 && (
                <p className="mt-4 text-xs text-muted-foreground italic md:hidden text-center">
                    Scorri la tabella lateralmente per vedere tutte le colonne →
                </p>
            )}

            {/* Table — always a real table, horizontal scroll on smartphone */}
            {filtered.length > 0 && (
                <div
                    className="mt-3 sm:mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm"
                    data-testid="maggioranze-table-wrap"
                >
                    <table className="w-full min-w-[900px] text-sm" data-testid="maggioranze-table">
                        <thead className="bg-slate-50 text-slate-700">
                            <tr>
                                <th className="text-left align-bottom px-4 py-3 font-semibold min-w-[240px]">
                                    Argomento
                                </th>
                                <th className="text-left align-bottom px-4 py-3 font-semibold min-w-[150px]">
                                    <div className="text-[11px] uppercase tracking-widest text-slate-500">Quorum Costitutivo</div>
                                    <div>Condòmini</div>
                                </th>
                                <th className="text-left align-bottom px-4 py-3 font-semibold min-w-[130px]">
                                    <div className="text-[11px] uppercase tracking-widest text-slate-500">Quorum Costitutivo</div>
                                    <div>Millesimi</div>
                                </th>
                                <th className="text-left align-bottom px-4 py-3 font-semibold min-w-[220px]">
                                    <div className="text-[11px] uppercase tracking-widest text-slate-500">Quorum Deliberativo</div>
                                    <div>Condòmini</div>
                                </th>
                                <th className="text-left align-bottom px-4 py-3 font-semibold min-w-[130px]">
                                    <div className="text-[11px] uppercase tracking-widest text-slate-500">Quorum Deliberativo</div>
                                    <div>Millesimi</div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filtered.map((r, i) => (
                                <tr key={i} data-testid={`maggioranze-row-${i}`} className="align-top hover:bg-slate-50/60 transition">
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-slate-900 leading-snug">{r.topic}</div>
                                        {r.ref && <div className="text-xs text-muted-foreground mt-0.5">{r.ref}</div>}
                                    </td>
                                    <td className="px-4 py-3 text-slate-800 font-medium">
                                        {COST_COND_1} <span className="text-muted-foreground font-normal">({COST_COND_2})</span>
                                    </td>
                                    <td className={`px-4 py-3 font-semibold ${toneOfMillesimi(COST_MILL_1)}`}>
                                        {COST_MILL_1} <span className="text-muted-foreground font-normal">({COST_MILL_2})</span>
                                    </td>
                                    <td className="px-4 py-3 text-slate-800 leading-snug">
                                        <span>{r.dc}</span>{" "}
                                        <span className="text-muted-foreground italic">(idem)</span>
                                    </td>
                                    <td className={`px-4 py-3 font-semibold ${toneOfMillesimi(r.m1)}`}>
                                        {r.m1}{" "}
                                        <span className="text-muted-foreground font-normal not-italic">
                                            (
                                            {r.m2 === "idem"
                                                ? <span className="italic">idem</span>
                                                : r.m2}
                                            )
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Footer note */}
            <p className="mt-10 text-xs text-muted-foreground leading-relaxed border-t border-slate-200 pt-5">
                <strong>Nota</strong>: per le voci non previste si rinvia alle norme di legge e agli usi locali. Questa guida ha valore informativo e non sostituisce il parere di un professionista qualificato.
            </p>

            {/* CTA */}
            <div className="mt-8 rounded-2xl bg-slate-950 text-white p-6 sm:p-8 text-center">
                <h3 className="font-serif text-2xl font-semibold">Hai un dubbio su una specifica delibera?</h3>
                <p className="mt-2 text-slate-300 text-sm">Ogni assemblea può presentare situazioni particolari. Chiedi al nostro esperto.</p>
                <Button asChild size="lg" className="mt-5 bg-accent text-accent-foreground hover:bg-accent/90 font-semibold rounded-full">
                    <Link to="/registrati">Fai una domanda</Link>
                </Button>
            </div>
        </div>
    );
}
